import { InfiniteTable, SaveButton } from '@/components/_common';
import { useCompanyFormContext } from '@/components/Companies/CompaniesFormProvider';
import { usePortoTaskList } from '@/core/domain/operational/porto-tasks/porto-tasks.hooks';
import {
  usePortoScheduleConfigurationAdd,
  usePortoScheduleConfigurationList,
} from '@/core/domain/workorder/porto-schedule/porto-schedule.hooks';
import { PageProps } from '@/types/PageProps';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { InputNumber, message, Skeleton, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

interface Service {
  id: number;
  name: string;
  code: string;
  duration?: number;
}

export default function CompanyPortoScheduleServices({ route }: PageProps) {
  const intl = useIntl();

  const { company } = useCompanyFormContext();

  const [services, setServices] = useState<Service[]>([]);
  const [indexError, setIndexError] = useState<number>();

  const portoTaskList = usePortoTaskList();
  const portoConfigurationList = usePortoScheduleConfigurationList(
    company?.capsCode,
  );
  const portoConfigurationAdd = usePortoScheduleConfigurationAdd();

  const isLoading = portoTaskList.isLoading || portoConfigurationList.isLoading;
  const addIsLoading = portoConfigurationAdd.isLoading;

  const portoTaks = flatInfiniteData(portoTaskList.data);

  useEffect(() => {
    setServices(() => {
      const tasks = portoTaks.map<Service>((task) => ({
        ...task,
      }));

      if (portoConfigurationList.data?.configurations?.length) {
        const configs = portoConfigurationList.data.configurations;

        return tasks.map((task) => {
          const taskFinded = configs.find(
            (config) => config.portoTaskId === task.id,
          );

          if (taskFinded) {
            return { ...task, duration: taskFinded.durationInMinutes };
          }

          return task;
        });
      }

      return tasks;
    });
  }, [portoTaskList.dataUpdatedAt, portoConfigurationList.dataUpdatedAt]);

  const handleSubmit = async () => {
    if (!company?.capsCode) return;

    setIndexError(undefined);

    const indexError = services.findIndex((service) => service.duration === 0);

    if (indexError >= 0) {
      setIndexError(indexError);
      return message.error('Não pode ser enviado serviços com duração zerada!');
    }

    await portoConfigurationAdd.mutateAsync({
      capsCode: company?.capsCode,
      serviceConfigurations: services
        .filter((service) => !!service.duration)
        .map((service) => ({
          durationInMinutes: service.duration as number,
          portoTaskId: service.id,
        })),
    });
  };

  const handleDurationService = (value: number, index: number) => {
    setServices((prev) =>
      prev.map((task, taskIndex) =>
        taskIndex === index ? { ...task, duration: value } : task,
      ),
    );
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <div className="page">
        <div className="page-header" style={{ marginBottom: '24px' }}>
          <Typography.Title level={3}>
            Serviços de agendamento Porto
          </Typography.Title>

          <div className="actions">
            <SaveButton onClick={handleSubmit} loading={addIsLoading}>
              Salvar
            </SaveButton>
          </div>
        </div>

        <p>
          Padronize o tempo de serviço configurando a duração do agendamento
          para padronização do modelo utilizado na plataforma de agendamentos da
          Porto.
        </p>

        <Skeleton loading={isLoading}>
          <InfiniteTable
            dataSource={services}
            columns={[
              { title: 'Serviço', dataIndex: 'name' },
              {
                title: 'Duração em minutos',
                render: (_, item, index) => (
                  <InputNumber
                    value={item.duration}
                    min={0}
                    onChange={(value) =>
                      handleDurationService(Number(value) || 0, index)
                    }
                    style={{
                      width: '100%',
                      borderColor: index === indexError ? 'red' : undefined,
                    }}
                    disabled={addIsLoading}
                    placeholder="60"
                    formatter={(value: unknown) => {
                      return value ? (Number(value) || 0)?.toFixed(0) : '';
                    }}
                  />
                ),
                width: 200,
              },
            ]}
            loading={portoTaskList.isLoading}
            isFetching={portoTaskList.isFetching}
            hasNextPage={portoTaskList.hasNextPage}
            fetchNextPage={portoTaskList.fetchNextPage}
          />
        </Skeleton>
      </div>
    </>
  );
}
